import { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Upload, FileSpreadsheet, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export function UploadStep({ file, parsing, onFileSelect, onBack, onNext, sheetNames = [], sheetName, onSheetChange, rowCount = 0, error }) {
  const fileInputRef = useRef(null);

  return (
    <div className="space-y-6" data-testid="upload-step">
      <Card data-testid="upload-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" /> Upload Spreadsheet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 transition-colors"
            onClick={() => !parsing && fileInputRef.current?.click()}
            data-testid="drop-zone"
          >
            {parsing ? (
              <div className="flex flex-wrap items-center justify-center gap-2 min-w-0">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm font-medium">Reading file...</span>
              </div>
            ) : file ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium break-all" data-testid="uploaded-csv-name">{file.name}</span>
                <Badge variant="secondary" className="text-xs">{(file.size / 1024).toFixed(1)} KB</Badge>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Choose a CSV or Excel file</p>
                <p className="text-xs text-muted-foreground mt-1">XLSX, XLS, CSV · up to 10 MB</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            disabled={parsing}
            className="hidden"
            onChange={onFileSelect}
            data-testid="csv-file-input"
          />
          {sheetNames.length > 0 && <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="migration-sheet-picker" data-testid="migration-sheet-label">Worksheet</label>
            <Select value={sheetName || ''} onValueChange={onSheetChange} disabled={parsing}>
              <SelectTrigger id="migration-sheet-picker" className="w-full" data-testid="migration-sheet-select"><SelectValue placeholder="Choose one sheet" /></SelectTrigger>
              <SelectContent data-testid="migration-sheet-options">{sheetNames.map((name, index) => <SelectItem key={name} value={name} data-testid={`migration-sheet-option-${index}`}>{name}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          {rowCount > 0 && !parsing && <p className="text-sm text-muted-foreground" data-testid="migration-row-count">{rowCount} data rows{sheetName ? ` · ${sheetName}` : ''}</p>}
          {error && <p className="text-sm text-destructive break-words" role="alert" data-testid="migration-file-error">{error}</p>}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" className="gap-1.5" onClick={onBack} data-testid="upload-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button className="gap-1.5" onClick={onNext} disabled={!file || parsing || !rowCount || Boolean(error)} data-testid="upload-next-btn">
          Next: Map Columns <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
